import { useIsomorphicLayoutEffect, useScroll } from "@react-spring/core"
import { animated } from "@react-spring/web"
import Link from "next/link"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import Footer from "../components/footer"
import Menu from "../components/menu"
import database from "../database.mjs"
import YouTube from "react-youtube"
import { NextSeo } from "next-seo"
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion"

function kFormatter(num) {
  num = Math.floor(num / 100) * 100

  const cleanString =
    Math.abs(num) > 999
      ? (Math.sign(num) * (Math.abs(num) / 1000).toFixed(1)).toString() + "k"
      : (Math.sign(num) * Math.abs(num)).toString()

  if (!cleanString.includes(".")) return `${cleanString.replace("k", "")}.0k`
  else return cleanString
}

export async function getServerSideProps() {
  const facebook = database
    .prepare("select value from socials where platform = 'facebook' order by timestamp desc limit 1")
    .get()

  const instagram = database
    .prepare("select value from socials where platform = 'instagram' order by timestamp desc limit 1")
    .get()

  const tiktok = database.prepare("select value from socials where platform = 'tiktok' order by timestamp desc limit 1").get()

  const properties = database
    .prepare(
      "select id, address, area, is_sold, price, beds, baths from properties_for_sale where is_sold = 1 order by timestamp desc limit 8"
    )
    .all()

  for (const property of properties) {
    const id = property?.["id"]
    const filename = database
      .prepare("select filename from properties_for_sale_photos where property_id = ? limit 1")
      .get(id)?.["filename"]

    property["thumbnail"] = `/api/images/${filename}.webp`
  }

  return {
    props: {
      properties,
      socials: {
        facebook: facebook?.value || null,
        tiktok: tiktok?.value || null,
        instagram: instagram?.value || null,
      },
    },
  }
}

function HMO({ properties, socials }) {
  const escFunction = useCallback(event => {
    if (event.key === "Escape") {
      setIsVideoOpen(false)
      document?.body?.classList?.remove("overflow-hidden")
    }
  }, [])

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false)

    return () => {
      document.removeEventListener("keydown", escFunction, false)
    }
  }, [escFunction])

  useEffect(() => {
    document?.body?.classList?.remove("overflow-hidden")
  }, [])

  const cards = useRef()
  const { scrollY } = useScroll()

  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const [showVideoOverlay, setShowVideoOverlay] = useState(false)

  useEffect(() => {
    if (!isVideoOpen) {
      setShowVideoOverlay(false)

      return
    }

    const handle = setTimeout(() => setShowVideoOverlay(true), 300)

    return () => clearTimeout(handle)
  }, [isVideoOpen])

  const [windowInnerHeight, setWindowInnerHeight] = useState(0)

  // useEffect(() => {
  //   const listener = () => {
  //     setWindowInnerHeight(window?.innerHeight)
  //     setCardsBox(cards?.current?.getBoundingClientRect())
  //     setHeight(content?.current?.getBoundingClientRect()?.height)
  //   }

  //   window.addEventListener("resize", listener)

  //   return () => window.removeEventListener("resize", listener)
  // }, [])

  const servicesCard = useRef()

  const videoContainer = useRef()

  const content = useRef()
  const [height, setHeight] = useState(0)

  const [videoBoundingBox, setVideoBoundingBox] = useState({
    top: 0,
    left: 0,
    right: 0,
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  })

  useIsomorphicLayoutEffect(() => {
    setHeight(content?.current?.getBoundingClientRect()?.height)
    setWindowInnerHeight(window.innerHeight)
  }, [])

  const portalsBox = useRef()

  const [cardsContainerBox, setCardsContainerBox] = useState({
    top: 0,
    left: 0,
    right: 0,
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  })

  useLayoutEffect(() => {
    setCardsContainerBox(cardsContainerRef?.current?.getBoundingClientRect())
  }, [])

  const handleScroll = () => {
    setCardsContainerBox(cardsContainerRef?.current?.getBoundingClientRect())
  }

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const cardsContainerRef = useRef()

  const [rotate1, setRotate1] = useState(false)
  const [rotate2, setRotate2] = useState(false)
  const [rotate3, setRotate3] = useState(false)
  const [rotate4, setRotate4] = useState(false)
  const [rotate5, setRotate5] = useState(false)
  const [rotate6, setRotate6] = useState(false)
  const [rotate7, setRotate7] = useState(false)
  const [rotate8, setRotate8] = useState(false)
  const [rotate9, setRotate9] = useState(false)
  const [rotate10, setRotate10] = useState(false)
  const [rotate11, setRotate11] = useState(false)
  const [rotate12, setRotate12] = useState(false)

  return (
    <>
      <NextSeo
        title="Guide to HMOs in Loughborough"
        description=" The Loughborough market for houses in multiple occupation (HMOs) differs significantly from the rest of the UK. Being aware of these variations can help you avoid expensive mistakes. To assist you in making informed
        choices, we have compiled this list of frequently asked questions (FAQs). Nevertheless, the information
        provided should not be regarded as legal advice."
      />
      <div className="overflow-hidden max-w-screen">
        <animated.div
          className="fixed top-0 left-0 w-screen min-h-screen bg-white"
          ref={content}
          style={{
            transform: scrollY.to(x => {
              // if (cardsContainerBox.top <= 0) {
              //   return `translateY(-${1200}px)`
              // }

              return `translateY(-${x}px)`
            }),
          }}
        >
          <div className="relative  h-[37.5vh] md:min-h-[768px] sm:h-[70vh] min-h-[720px] lg:min-h-[540px]">
            <img className="object-cover w-full h-full select-none" src="/images/hmo.jpg" />
            <div className="absolute top-0 bottom-0 left-0 right-0">
              <div className="absolute top-0 bottom-0 left-0 right-0 flex flex-col items-center justify-center opacity-50 bg-gradient-to-b from-transparent to-stone-900"></div>
              <div className="absolute left-0 right-0 flex items-center justify-center px-4 bottom-36 drop-shadow">
                <div className="w-full sm:max-w-screen-lg md:max-w-screen-lg">
                  <h1 className="animate__animated animate__fadeIn font-serif text-[2.75rem] leading-none font-semibold text-center text-white sm:text-6xl xs:text-3xl mt-6 opacity-100">
                    Guide to HMOs in Loughborough
                  </h1>
                  <p className="mt-4 text-sm text-center text-white opacity-100 md:text-base drop-shadow">
                    The Loughborough market for houses in multiple occupation (HMO) differs significantly from the rest of the
                    UK. Being aware of these variations can help you avoid expensive mistakes. To assist you in making informed
                    choices, we have compiled this list of frequently asked questions. Nevertheless, the information provided
                    should not be regarded as legal advice.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div id="faqs" className="flex items-center justify-center h-full px-4 pb-12 mt-6 bg-white text-stone-900">
              <div className="flex flex-col items-center justify-center w-full lg:flex-row sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl gap-x-8">
                <Accordion
                  className="flex flex-col items-start justify-center flex-1 w-full lg:flex-row gap-x-8"
                  allowZeroExpanded
                >
                  <div className="flex flex-col justify-center flex-1 w-full h-full">
                    <AccordionItem className="w-full h-full">
                      <AccordionItemHeading
                        onClick={() => {
                          setRotate1(rotate1 => !rotate1)

                          setRotate2(false)
                          setRotate3(false)
                          setRotate4(false)
                          setRotate5(false)
                          setRotate6(false)
                          setRotate7(false)
                          setRotate8(false)
                          setRotate9(false)
                          setRotate10(false)
                          setRotate11(false)
                          setRotate12(false)
                        }}
                      >
                        <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                          <div className="mt-1">
                            <div className={`${rotate1 ? "rotate-45" : "rotate-0"} transition-all duration-300`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </div>
                          </div>
                          <p>What is a House in Multiple Occupation (HMO)?</p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-6">
                        <p>
                          According to both planning and licensing definitions, a house in multiple occupation (HMO) is defined
                          as “a building which is occupied by three or more people forming two or more households”. It is
                          important to note that students within a group beyond are always considered to be separate households.
                          The legislation is more concerned with lifestyle than how the contracts are constructed.
                        </p>
                      </AccordionItemPanel>
                    </AccordionItem>
                    <AccordionItem>
                      <AccordionItemHeading
                        onClick={() => {
                          setRotate2(rotate2 => !rotate2)

                          setRotate1(false)
                          setRotate3(false)
                          setRotate4(false)
                          setRotate5(false)
                          setRotate6(false)
                          setRotate7(false)
                          setRotate8(false)
                          setRotate9(false)
                          setRotate10(false)
                          setRotate11(false)
                          setRotate12(false)
                        }}
                      >
                        <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                          <div className="mt-1">
                            <div className={`transition-all duration-300 ${rotate2 ? "rotate-45" : "rotate-0"}`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </div>
                          </div>
                          <p>Will my HMO need a licence?</p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-6">
                        <p>
                          It is essential to understand that licensing and planning consent are two separate things, and you
                          always need both in Loughborough. You can buy a property with planning consent, but licences are
                          non-transferable, and you must apply for your own.
                        </p>
                        <p className="mt-4">
                          On the 1st of April 2023, Charnwood Borough Council introduced an Additional Licencing Scheme covering
                          all of Charnwood. Under this new scheme, all HMOs (three or more persons forming two or more
                          households) will require a licence regardless of the number of bedrooms. Failing to have a licence can
                          result in a financial penalty notice of up to £30,000 or an unlimited fine from the courts.
                        </p>
                        <p className="mt-4">
                          You can read more about this and apply{" "}
                          <Link
                            className="font-medium text-teal-900"
                            target="_blank"
                            href="https://www.charnwood.gov.uk/pages/applying_for_a_property_licence"
                          >
                            here
                          </Link>
                          .
                        </p>
                      </AccordionItemPanel>
                    </AccordionItem>
                    <AccordionItem>
                      <AccordionItemHeading
                        onClick={() => {
                          setRotate3(rotate3 => !rotate3)

                          setRotate1(false)
                          setRotate2(false)
                          setRotate4(false)
                          setRotate5(false)
                          setRotate6(false)
                          setRotate7(false)
                          setRotate8(false)
                          setRotate9(false)
                          setRotate10(false)
                          setRotate11(false)
                          setRotate12(false)
                        }}
                      >
                        <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                          <div className="mt-1">
                            <div className={`transition-all duration-300 ${rotate3 ? "rotate-45" : "rotate-0"}`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </div>
                          </div>
                          <p>Professional or Student HMOs?</p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-6">
                        <p>
                          Student HMOs typically work on an annual cycle, with tenancies starting on either the 1st of July or
                          the 1st of September. Advertising starts in October to secure tenants for the following year. Most of
                          these are let on single agreements to a group of students. They are typically more financially secure
                          than professional HMO tendencies as you usually have guarantors in place for each occupant.
                        </p>
                        <p className="mt-4">
                          Professional HMOs are usually let on an individual room basis as and when they become vacant. Thus
                          these occupants rarely know each other before moving in. Whilst these tenants are often employed, they
                          are usually relatively low income, thus a higher financial risk than students with individual parental
                          guarantors. Although they generally have longer tenancies and lower wear and tear than student
                          properties. They also spread the risk of occupancy. Failure to let a student property by the start of
                          the academic year could be financially catastrophic. One or two rooms, less so.
                        </p>
                      </AccordionItemPanel>
                    </AccordionItem>
                    <AccordionItem>
                      <AccordionItemHeading
                        onClick={() => {
                          setRotate4(rotate4 => !rotate4)

                          setRotate1(false)
                          setRotate2(false)
                          setRotate3(false)
                          setRotate5(false)
                          setRotate6(false)
                          setRotate7(false)
                          setRotate8(false)
                          setRotate9(false)
                          setRotate10(false)
                          setRotate11(false)
                          setRotate12(false)
                        }}
                      >
                        <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                          <div className="mt-1">
                            <div className={`transition-all duration-300 ${rotate4 ? "rotate-45" : "rotate-0"}`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </div>
                          </div>
                          <p>Can you sum this up?</p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-6">
                        <p>
                          An HMO is defined as “a building which is occupied by three or more people forming two or more
                          households”. Students are always considered to be separate households. All HMO&#39;s in Loughborough
                          need either expressed planning consent or continuous use since 2011 due to the Article 4 directive.
                          All HMOs in Charnwood need a licence, irrespective of how many people occupy the property. They are
                          typically much more management intensive than conventional rentals, although the larger ones can yield
                          excellent returns.
                        </p>
                      </AccordionItemPanel>
                    </AccordionItem>
                  </div>
                  <div className="flex flex-col justify-center flex-1 w-full h-full">
                    <AccordionItem className="w-full h-full">
                      <AccordionItemHeading
                        onClick={() => {
                          setRotate7(rotate7 => !rotate7)

                          setRotate1(false)
                          setRotate2(false)
                          setRotate3(false)
                          setRotate4(false)
                          setRotate5(false)
                          setRotate6(false)

                          setRotate8(false)
                          setRotate9(false)
                          setRotate10(false)
                          setRotate11(false)
                          setRotate12(false)
                        }}
                      >
                        <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                          <div className="mt-1">
                            <div className={`transition-all duration-300 ${rotate7 ? "rotate-45" : "rotate-0"}`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </div>
                          </div>
                          <p>What is Loughborough’s Article 4 directive on HMOs?</p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-6">
                        <p>
                          Since 2011 Loughborough has had an Article 4 directive covering the whole town. This removed people's
                          customary permitted development rights to change the use of properties from planning Use class C3
                          (single dwelling house) Use class C4 (small HMO). Thus, most HMOs in Loughborough are ones that
                          existed before the directive came into force. New HMOs can still be created, although they now require
                          full planning consent. The most common and complex planning objections to overcome are of area
                          saturation and parking.
                        </p>
                        <p className="mt-4">
                          The economic impact of this control is that HMO properties now demand greatly more rent than
                          conventional properties because of the effect on supply and demand ratios.
                        </p>
                        <p className="mt-4">
                          If you are considering buying an existing HMO, ensure that the property either has express planning
                          consent as a C4 small HMO or has proven uninterrupted historic use since 2011. This typically comes as
                          a CLUED (Certificate of Lawfulness for Existing Use or Development). HMOs with more than six occupants
                          are classed as ‘Sui Generis’ and have always needed planning consent.
                        </p>
                      </AccordionItemPanel>
                    </AccordionItem>
                    <AccordionItem>
                      <AccordionItemHeading
                        onClick={() => {
                          setRotate8(rotate8 => !rotate8)

                          setRotate1(false)
                          setRotate2(false)
                          setRotate3(false)
                          setRotate4(false)
                          setRotate5(false)
                          setRotate6(false)
                          setRotate7(false)
                          setRotate9(false)
                          setRotate10(false)
                          setRotate11(false)
                          setRotate12(false)
                        }}
                      >
                        <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                          <div className="mt-1">
                            <div className={`transition-all duration-300 ${rotate8 ? "rotate-45" : "rotate-0"}`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </div>
                          </div>
                          <p>Should I buy a HMO?</p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-6">
                        <p>
                          HMOs that are put up for sale are typically priced based on their income potential rather than
                          traditional property valuation methods. In other words, their prices are determined by the amount of
                          rental income they generate. Thus, converting a C3 single-dwelling house into a C4 small HMO can
                          potentially be more financially rewarding compared to purchasing an existing HMO.
                        </p>
                        <p className="mt-4">
                          HMOs are also significantly more management intensive. The wear and tear on the property can be more
                          significant due to the higher number of occupants. Additionally, tenancies in HMOs tend to be shorter
                          in duration, and the turnover of tenants may be more frequent. Managing HMOs also involves dealing
                          with a larger number of occupants, often including young individuals and their parents. HMOs are not
                          for beginners. Successfully managing HMOs requires a solid understanding of the regulations, efficient
                          maintenance practices, and effective communication with tenants.
                        </p>
                      </AccordionItemPanel>
                    </AccordionItem>
                    <AccordionItem>
                      <AccordionItemHeading
                        onClick={() => {
                          setRotate9(rotate9 => !rotate9)

                          setRotate1(false)
                          setRotate2(false)
                          setRotate3(false)
                          setRotate4(false)
                          setRotate5(false)
                          setRotate6(false)
                          setRotate7(false)
                          setRotate8(false)
                          setRotate10(false)
                          setRotate11(false)
                          setRotate12(false)
                        }}
                      >
                        <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                          <div className="mt-1">
                            <div className={`${rotate9 ? "rotate-45" : "rotate-0"} transition-all duration-300`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </div>
                          </div>
                          <p>Do all HMOs offer the best financial returns?</p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-6">
                        <p>
                          When it comes to HMOs, bigger properties tend to be more advantageous. Due to the added costs involved
                          in running an HMO, smaller two and three-bed HMOs may not always be financially viable. In such cases,
                          it might be more beneficial to rent them out as traditional single-family homes. On the other hand,
                          larger HMOs with five or more bedrooms typically yield excellent returns compared to single dwellings.
                        </p>
                      </AccordionItemPanel>
                    </AccordionItem>
                    <AccordionItem>
                      <AccordionItemHeading
                        onClick={() => {
                          setRotate10(rotate10 => !rotate10)

                          setRotate1(false)
                          setRotate2(false)
                          setRotate3(false)
                          setRotate4(false)
                          setRotate5(false)
                          setRotate6(false)
                          setRotate7(false)
                          setRotate8(false)
                          setRotate9(false)
                          setRotate11(false)
                          setRotate12(false)
                        }}
                      >
                        <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                          <div className="mt-1">
                            <div className={`${rotate10 ? "rotate-45" : "rotate-0"} transition-all duration-300`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                              </svg>
                            </div>
                          </div>
                          <p>Why Huntleys?</p>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-6">
                        <p>
                          Huntleys has earned a strong reputation for providing top-quality student and professional HMO
                          lettings and management services in Loughborough. We create stunning digital particulars highlighting
                          the individual attributes of every property we market, with video tours and floorplans as standard. As
                          a result, Huntleys have become Loughborough and Charnwood's most followed estate and letting agent.
                        </p>
                        <p className="mt-4">
                          We assist landlords in navigating the complex landscape of regulations while offering comprehensive
                          services to find and manage tenancies. At Huntleys, we take pride in keeping landlords well- informed
                          about current and future market trends, ensuring they stay ahead of the game. Our ability to deliver
                          such outstanding service is made possible by a team of passionate and highly enthusiastic individuals
                          who take pride in their work. Visit our offices in Loughborough town centre to meet the team and find
                          out what we can do for you.
                        </p>
                      </AccordionItemPanel>
                    </AccordionItem>
                  </div>
                </Accordion>
              </div>
            </div>
          </div>
          <div className="flex justify-center px-4 mb-12 bg-white">
            <div className="flex items-center justify-center flex-1 w-full overflow-hidden sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
              <div className="grid flex-1 w-full gap-6 xl:gap-8 md:grid-cols-2 xl:grid-cols-5">
                <Link href="/docs/agency-agreement.pdf" target="_blank" prefetch={false}>
                  <button className="flex items-center justify-center w-full p-4 font-medium border-2 rounded gap-x-4 text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                      />
                    </svg>
                    <p>Agency Agreement</p>
                  </button>
                </Link>
                <Link href="/valuation?type=let" prefetch={false}>
                  <button className="flex items-center justify-center w-full p-4 font-medium border-2 rounded gap-x-4 text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink">
                    <p>Arrange a valuation</p>
                  </button>
                </Link>
                <Link href="tel:+441509320320" prefetch={false}>
                  <button className="flex items-center justify-center w-full p-4 font-medium border-2 rounded gap-x-4 text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                      />
                    </svg>

                    <p>01509 320 320</p>
                  </button>
                </Link>
                <Link href="mailto:let@huntleys.net" prefetch={false}>
                  <button className="flex items-center justify-center w-full p-4 font-medium border-2 rounded gap-x-4 text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                      />
                    </svg>

                    <p>let@huntleys.net</p>
                  </button>
                </Link>
                <Link href="/rental-yield-calculator" prefetch={false}>
                  <button
                    aria-label="Rental Yield Calculator"
                    className="flex items-center justify-center w-full p-4 font-medium border-2 rounded gap-x-4 text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z"
                      />
                    </svg>
                    <p>Yield Calculator</p>
                  </button>
                </Link>
              </div>
            </div>
          </div>
          <div>
            <div id="services" ref={servicesCard} className="px-4 py-12 bg-cpink">
              <h1
                className="mx-4 font-serif text-3xl font-bold text-center text-stone-900 sm:text-5xl drop-shadow"
                style={{ lineHeight: 1.125 }}
              >
                Letting services
              </h1>
              <div className="w-full mx-auto mt-12 sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                <div className="flex flex-col flex-wrap items-center gap-8 text-white md:grid-cols-2 xl:grid-cols-3 md:grid xl:flex-row lg:gap-12">
                  <div className="flex-1 w-full h-full overflow-hidden bg-white rounded drop-shadow-sm">
                    <div className="p-4 bg-teal-900">
                      <h1 className="text-lg font-medium ">Find a Tenant</h1>
                      <div className="flex items-center">
                        <p className="text-xl font-medium text-white">£</p>
                        <h1 className="ml-1 text-5xl font-medium">225</h1>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-white">per room</p>
                          <p className="text-sm font-medium opacity-0 text-cpink">inc. VAT</p>
                        </div>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-cpink">inc. VAT</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col p-4 bg-white gap-y-2">
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Full marketing campaign inc Floorplans</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Video tours</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Prequalification of applicants</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Conduct viewings and provide feedback</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Negotiate the best deal for you</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Provide you with a referencing report</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Produce a comprehensive tenancy agreement</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Set up a standing order</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Secure funds and handover keys</p>
                      </div>
                    </div>
                    <div className="flex flex-col justify-end p-4 border-t border-stone-200">
                      <Link href="/valuation?type=let">
                        <button className="w-full px-4 py-3 font-medium transition-all duration-300 bg-teal-800 border-2 border-teal-900 rounded select-none text-teal-50 hover:bg-teal-900">
                          Select
                        </button>
                      </Link>
                    </div>
                  </div>
                  <div className="flex-1 w-full overflow-hidden bg-white rounded drop-shadow-sm">
                    <div className="p-4 bg-teal-900">
                      <h1 className="text-lg font-medium ">Rent Collection</h1>
                      <div className="flex items-center">
                        <p className="text-xl font-medium text-white">£</p>
                        <h1 className="ml-1 text-5xl font-medium">150</h1>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-white">per room</p>
                          <p className="text-sm font-medium opacity-0 text-cpink">inc. VAT</p>
                        </div>
                        <p className="ml-2 text-xl font-medium text-cpink">+</p>
                        <h1 className="ml-2 text-5xl font-medium">6%</h1>
                        <p className="ml-2 font-medium text-cpink">inc. VAT</p>
                      </div>
                    </div>
                    <div className="flex flex-col p-4 bg-white gap-y-2">
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Our tenant find service +</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Register the Tenant’s deposit</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Collect and process all rent payments</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Contact the tenant regarding any rent arrears</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Advise on action when a tenant doesn't pay</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Produce and email monthly statements</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Produce annual summaries of account</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Negotiate any renewal or extension</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Serve any required notices</p>
                      </div>
                    </div>
                    <div className="p-4 bg-white border-t border-stone-200">
                      <Link href="/valuation?type=let">
                        <button className="w-full px-4 py-3 font-medium transition-all duration-300 bg-teal-800 border-2 border-teal-900 rounded select-none text-teal-50 hover:bg-teal-900">
                          Select
                        </button>
                      </Link>
                    </div>
                  </div>
                  <div className="flex-1 w-full h-full overflow-hidden bg-white rounded drop-shadow-sm">
                    <div className="p-4 bg-teal-900">
                      <h1 className="text-lg font-medium ">Fully Managed</h1>
                      <div className="flex items-center">
                        <p className="text-xl font-medium text-white">£</p>
                        <h1 className="ml-1 text-5xl font-medium">150</h1>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-white">per room</p>
                          <p className="text-sm font-medium opacity-0 text-cpink">inc. VAT</p>
                        </div>
                        <p className="ml-2 text-xl font-medium text-cpink">+</p>
                        <h1 className="ml-2 text-5xl font-medium">9%</h1>
                        <p className="ml-2 font-medium text-cpink">inc. VAT</p>
                      </div>
                    </div>
                    <div className="flex flex-col p-4 bg-white gap-y-2">
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Our rent collection service +</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Produce an Inventory & Condition Report</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Record the Council tax and Utility liability</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Quarterly property visits</p>
                      </div>

                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Manage maintenance Arranges (no mark-up)</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Act as the tenant's point of contact</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Manage any statutory safety checks</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Conduct a checkout inspection</p>
                      </div>
                      <div className="flex items-center gap-x-2 text-stone-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-teal-900"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <p className="font-medium">Negotiate any deposit deductions</p>
                      </div>
                    </div>
                    <div className="p-4 bg-white border-t border-stone-200">
                      <Link href="/valuation?type=let">
                        <button className="w-full px-4 py-3 font-medium transition-all duration-300 bg-teal-800 border-2 border-teal-900 rounded select-none text-teal-50 hover:bg-teal-900">
                          Select
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div ref={videoContainer} className="px-4 py-12 mt-8 bg-white">
            <div className="w-full mx-auto sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
              <div className={[`relative flex w-full mx-auto rounded aspect-video max-h-[65vh]`].join(" ")}>
                <div className="w-full h-full overflow-hidden rounded max-h-[65vh]">
                  <div className="absolute top-0 left-0 right-0 h-24 rounded-t opacity-75 bg-gradient-to-b from-stone-900 to-transparent"></div>
                  <p className="absolute text-white drop-shadow-md top-8 left-6 lg:top-12 lg:left-16">Let with Huntleys</p>
                  <div className="absolute bottom-0 left-0 right-0 h-32 rounded-b opacity-75 bg-gradient-to-t from-stone-900 to-transparent"></div>
                  <div className="absolute left-8 bottom-6 lg:bottom-12 lg:left-16">
                    <h1 className="font-serif text-2xl font-medium text-white sm:text-3xl drop-shadow lg:text-4xl">
                      Kyfer Leech
                    </h1>
                    <p className="text-white drop-shadow-md">Lettings Valuer</p>
                  </div>
                  <div className="absolute top-0 bottom-0 left-0 right-0 flex">
                    <button
                      className={[
                        `flex p-4 m-auto text-white transition-all duration-300 border-2 border-white rounded-full hover:text-teal-900 hover:bg-white md:p-6 drop-shadow md:border-4`,
                      ].join(" ")}
                      onClick={() => {
                        document.body.classList.add("overflow-hidden")
                        setIsVideoOpen(false)
                        setShowVideoOverlay(true)
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-5 h-5 m-auto md:h-6 md:w-6"
                      >
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    </button>
                  </div>
                  <img className="object-cover w-full h-full rounded select-none" src="/images/let-thumbnail.webp" />
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-6 px-4 py-10 mt-10 select-none border-stone-900 bg-cpink">
            <h1
              className="z-30 mx-4 font-serif text-3xl font-bold text-center text-stone-900 sm:text-5xl"
              style={{ lineHeight: 1.125 }}
            >
              Get your property seen
            </h1>
            <div className="flex flex-col gap-10 mt-6 2xl:flex-row">
              <div className="flex-2">
                <p className="text-sm font-medium tracking-wide select-text text-stone-900">PROPERTY PORTALS</p>
                <div className="flex flex-wrap items-center gap-6 mt-3 xs:gap-2 sm:gap-12">
                  <img className="xs:h-2 h-7 sm:h-8" src="/images/rightmove.svg" />
                  <img className="h-5 xs:h-2 sm:h-6" src="/images/zoopla.svg" />
                  <img className="xs:h-2 h-7 sm:h-8" src="/images/onthemarket.svg" />
                  <img className="xs:h-2 h-7 sm:h-10" src="/images/primelocation.svg" />
                </div>
              </div>
              <div className="flex-2">
                <p className="w-full text-sm font-medium tracking-wide select-text text-stone-900">SOCIAL MEDIA</p>
                <div className="flex flex-wrap items-center gap-6 mt-5 xs:gap-2 sm:gap-12">
                  <img className="h-4 xs:h-2 sm:h-5" src="/images/facebook.svg" />
                  <img className="h-5 xs:h-2 sm:h-5" src="/images/tiktok.svg" />
                  <img className="h-5 xs:h-2 sm:h-5" src="/images/instagram.svg" />
                  <img className="h-5 xs:h-2 sm:h-5" src="/images/youtube.svg" />
                </div>
              </div>
              <div className="flex-1">
                <p className="w-full text-sm font-medium tracking-wide select-text text-stone-900">TARGETED ADVERTISING</p>
                <div className="flex items-center gap-6 mt-5 xs:gap-x-2 sm:gap-12">
                  <img className="h-5 xs:h-5 sm:h-5" src="/images/gads.svg" />
                  <img className="h-4 xs:h-4 sm:h-4" src="/images/fads.svg" />
                </div>
              </div>
            </div>
          </div>
          <div ref={cardsContainerRef} className="flex justify-center px-4 border-t py-14">
            <div className="w-full overflow-hidden rounded shadow-md sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
              <div className="mb-0 max-h-[36rem] w-full rounded overflow-hidden">
                <div className="max-h-[52rem] w-full rounded overflow-hidden">
                  <div ref={cards} className="flex flex-col items-start justify-start w-full h-full overflow-hidden rounded">
                    <div className="min-h-[36rem] rounded w-full h-full max-h-[36rem] mb-[254px] min-w-full overflow-hidden mx-auto  sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                      <div
                        className={`flex flex-col gap-y-12 lg:flex-row min-h-[36rem] max-h-[36rem] h-full overflow-hidden items-center w-full p-12 md:p-16 mx-auto rounded bg-teal-900 sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl gap-x-16 relative`}
                      >
                        {/* <div className="absolute top-0 bottom-0 left-0 right-0 flex flex-col justify-between p-8 pb-16 overflow-hidden gap-y-12 lg:flex-row">
                          <div className="flex items-center justify-center flex-1 h-56 lg:h-auto">
                            <img
                              className="block object-contain w-full h-full select-none max-h-96"
                              src="/images/drone-3.png"
                            />
                          </div>
                          <div className="flex-1 lg:p-12">
                            <div className="flex flex-col justify-center w-full h-full">
                              <h1 className="font-serif text-5xl font-semibold text-teal-900 md:text-6xl">Drone footage</h1>
                              <p className="mt-6 text-base text-teal-900 sm:text-lg">
                                Drone footage can give buyers a unique perspective of your property, differentiating your
                                listing from others. In addition, this footage is more likely to be shared on social media and
                                other online platforms, helping you to reach a larger audience of potential buyers.
                              </p>
                            </div>
                          </div>
                        </div> */}
                        <div className="absolute top-0 bottom-0 left-0 right-0 flex flex-col justify-between p-4 pb-16 overflow-hidden sm:p-8 gap-y-12 lg:flex-row">
                          <div className="flex items-center justify-center flex-1 h-56 lg:h-auto">
                            <img
                              alt="camera gimbal"
                              className="block object-contain w-full h-full select-none max-h-96"
                              src="/images/gimbal.png"
                            />
                          </div>
                          <div className="flex-1 lg:p-12">
                            <div className="flex flex-col justify-center w-full h-full">
                              <h1 className="font-serif text-5xl font-semibold text-cpink md:text-6xl">Video</h1>
                              <p className="mt-6 text-sm text-cpink sm:text-lg">
                                Video tours are a great way to show potential tenants around your home and give them a feel for
                                the space by giving them a more immersive experience. They also reach a wider audience and
                                generate more interest when shared on Social Media, helping to let your house faster and for
                                more money.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <animated.div
                        // style={{
                        //   transform: scrollY.to(x => {
                        //     if (x <= cardsBox.top - 87.5) return "translateY(0)"

                        //     // return `translateY(-${Math.min(
                        //     //   cardsBox?.height - 254,
                        //     //   x + cardsBox.top - cardsBox.height - 254
                        //     // )}px)`
                        //   }),
                        // }}
                        className={`flex mb-[254px] py-[254px] flex-col gap-y-12 sm:flex-row min-h-[36rem] max-h-[36rem] h-full overflow-hidden items-center w-full p-12 sm:p-16 mx-auto rounded bg-stone-900 sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl gap-x-16 relative`}
                      >
                        <div className="absolute top-0 bottom-0 left-0 right-0 flex flex-col justify-between p-8 pb-16 overflow-hidden gap-y-12 lg:flex-row">
                          <div className="flex items-center justify-center flex-1 h-56 lg:h-auto">
                            <img className="block object-contain w-full h-full select-none max-h-96" src="/images/gimbal.png" />
                          </div>
                          <div className="flex-1 lg:p-12">
                            <div className="flex flex-col justify-center w-full h-full">
                              <h1 className="font-serif text-5xl font-semibold text-cpink md:text-6xl">Video</h1>
                              <p className="mt-6 text-base text-cpink sm:text-lg">
                                Video tours are a great way to show potential buyers around your home and give them a feel for
                                the space by giving them a more immersive experience. They also reach a wider audience and
                                generate more interest when shared on Social Media, helping to sell your house faster and for
                                more money.
                              </p>
                            </div>
                          </div>
                        </div>
                      </animated.div>
                      <animated.div
                        // style={{
                        //   transform: scrollY.to(x => {
                        //     return `translateY(-${Math.min(cardsBox?.height * 2 - 254, x - windowInnerHeight)}px)`
                        //   }),
                        // }}
                        className={`flex flex-col gap-y-12 sm:flex-row min-h-[36rem] max-h-[36rem] h-full overflow-hidden items-center w-full p-12 sm:p-16 mx-auto rounded bg-teal-900 sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl gap-x-16 relative`}
                      >
                        <div className="absolute top-0 bottom-0 left-0 right-0 flex flex-col justify-between p-8 pb-16 overflow-hidden gap-y-12 lg:flex-row">
                          <div className="flex items-center justify-center flex-1 h-56 lg:h-auto">
                            <img
                              className="block object-contain w-full h-full select-none max-h-96"
                              src="/images/floorplans-4.png"
                            />
                          </div>
                          <div className="flex-1 lg:p-12">
                            <div className="flex flex-col justify-center w-full h-full">
                              <h1 className="font-serif text-5xl font-semibold text-cpink md:text-6xl">Floorplans</h1>
                              <p className="mt-6 text-base text-cpink sm:text-lg">
                                Using floor plans helps potential buyers understand the space's flow and potential, increasing
                                its appeal. Additionally, by showcasing the size and configuration of rooms and how they relate
                                to one another, floor plans can save you time by eliminating unsuitable viewings.
                              </p>
                            </div>
                          </div>
                        </div>
                      </animated.div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <section>
            <div className="relative flex bg-white">
              <div className="justify-center hidden w-full mx-auto max-h-64 sm:flex sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                <div className="absolute bottom-0 left-0 right-0 flex top-12 bg-cpink"></div>
                <div className="z-10 items-center justify-center w-1/4 px-4 -mt-48 sm:-mt-0 sm:-mb-32 sm:w-2/5 md:-mb-48 lg:w-1/6 xl:w-2/7">
                  <img alt="Phone" className="w-full select-none" src="/images/phone-3.png" />
                  <img alt="Laptop" className="w-full select-none sm:hidden" src="/images/phone-3.png" />
                </div>
                <div className="z-10 flex w-3/5 pt-12 md:py-12 md:pt-[4.75rem]">
                  <div className="px-4 my-auto">
                    <h2 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                      Find out what your property is worth
                    </h2>
                    <Link href="/valuation?type=let" target="_blank">
                      <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                        Arrange a valuation
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white sm:hidden">
              <div className="flex justify-center px-4 sm:hidden">
                <img alt="Laptop" className="w-full max-w-sm select-none" src="/images/phone-3.png" />
              </div>
              <div className="relative px-4 -mt-96 bg-cpink">
                <div className="flex py-12 ">
                  <div className="flex-1 min-w-0 sm:flex-2">
                    <h2 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                      Find out what your property is worth
                    </h2>
                    <Link href="/valuation?type=let" target="_blank">
                      <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                        Arrange a Valuation
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </section>
        </animated.div>
        <div
          style={{
            height: height,
          }}
        ></div>
        <Menu />
      </div>
      {showVideoOverlay && (
        <div className={[`fixed z-50 top-0 bottom-0 left-0 right-0 flex flex-col w-screen h-screen bg-stone-900`].join(" ")}>
          <div className="flex justify-between w-full p-4 sm:px-4 text-stone-900 bg-cpink border-stone-800">
            <p className="text-md">Let with Huntleys</p>
            <button
              className="transition-all duration-300"
              onClick={() => {
                setIsVideoOpen(false)
                setShowVideoOverlay(false)
                document.body.classList.remove("overflow-hidden")
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
          <div className="w-full h-full">
            <YouTube
              videoId="Ky_TDWvKBmQ"
              opts={{
                height: "100%",
                width: "100%",
                playerVars: {
                  autoplay: 1,
                  color: "white",
                },
              }}
              className="w-full h-full overflow-hidden"
            />
          </div>
        </div>
      )}
    </>
  )
}

export default HMO

